<template>
  <DashboardWidget
    title="My Tasks Today"
    icon="fa fa-check-square-o"
    :view-all-link="{ name: 'ToDo' }"
    :loading="loading"
    :is-empty="incompleteTasks.length === 0"
    empty-icon="fa fa-check-circle-o"
    empty-title="No tasks due today"
    empty-text="You're all caught up!"
  >
    <div class="tasks-summary">
      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-item">
          <span class="stat-value" :class="{ 'has-items': incompleteTasks.length > 0 }">{{ incompleteTasks.length }}</span>
          <span class="stat-label">Due Today</span>
        </div>
        <div class="stat-item">
          <span class="stat-value completed">{{ completedTasks.length }}</span>
          <span class="stat-label">Completed</span>
        </div>
        <div class="stat-item">
          <span class="stat-value overdue">{{ overdueTasks.length }}</span>
          <span class="stat-label">Overdue</span>
        </div>
      </div>

      <!-- Task List -->
      <div class="tasks-list" v-if="incompleteTasks.length > 0">
        <div
          v-for="task in displayedTasks"
          :key="task.id"
          class="list-item task-item"
          @click="editTask(task)"
        >
          <div class="item-avatar task-avatar" :class="{ overdue: isOverdue(task) }">
            <i class="fa fa-check"></i>
          </div>
          <div class="item-content">
            <div class="item-header">
              <span class="item-title">{{ getTaskContent(task) }}</span>
              <span class="item-time" :class="{ overdue: isOverdue(task) }">
                {{ getTimeDisplay(task) }}
              </span>
            </div>
            <p class="item-preview" v-if="task.patientName || getAssigneeName(task)">
              <template v-if="task.patientName">
                <i class="fa fa-user-o"></i> {{ task.patientName }}
              </template>
              <template v-if="getAssigneeName(task)">
                <span v-if="task.patientName" class="separator">&bull;</span>
                <i class="fa fa-user"></i> {{ getAssigneeName(task) }}
              </template>
            </p>
          </div>
        </div>
      </div>
    </div>
  </DashboardWidget>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import DashboardWidget from './DashboardWidget.vue';
import mixin from '../../../mixin.js';
import moment from 'moment-timezone';

export default {
  name: 'MyTasksWidget',

  components: {
    DashboardWidget
  },

  mixins: [mixin],

  props: {
    maxItems: {
      type: Number,
      default: 3
    }
  },

  data() {
    return {
      isLoadingTasks: false
    };
  },

  computed: {
    ...mapState({
      myCreatedTasks: state => state.tasks.myCreatedTasks,
      assignedToMeTasks: state => state.tasks.assignedToMeTasks,
      employees: state => state.customer.employees
    }),

    ...mapGetters({
      todaysTasksForEmployee: 'tasks/todaysTasksForEmployee'
    }),

    loading() {
      return this.isLoadingTasks;
    },

    allMyTasks() {
      return this.todaysTasksForEmployee ? this.todaysTasksForEmployee(this.userID) : [];
    },

    incompleteTasks() {
      // Filter tasks that are not completed (status is INCOMPLETE)
      return this.allMyTasks.filter(t => t.status === 'INCOMPLETE' || !t.completed);
    },

    completedTasks() {
      return this.allMyTasks.filter(t => t.status === 'COMPLETE' || t.completed);
    },

    overdueTasks() {
      const now = moment();
      return this.incompleteTasks.filter(t => {
        const dueDate = t.dueAtStr || t.dueDate;
        if (!dueDate) return false;
        return moment(dueDate).isBefore(now, 'day');
      });
    },

    displayedTasks() {
      // Sort by due date, overdue first
      return [...this.incompleteTasks]
        .sort((a, b) => {
          const aOverdue = this.isOverdue(a);
          const bOverdue = this.isOverdue(b);
          if (aOverdue && !bOverdue) return -1;
          if (!aOverdue && bOverdue) return 1;
          return 0;
        })
        .slice(0, this.maxItems);
    }
  },

  methods: {
    ...mapActions('tasks', ['getTaskDetails']),

    isOverdue(task) {
      const dueDate = task.dueAtStr || task.dueDate;
      if (!dueDate) return false;
      return moment(dueDate).isBefore(moment(), 'day');
    },

    getTimeDisplay(task) {
      // Show creation time if task was created today
      if (task.createdAt) {
        const created = moment(task.createdAt);
        if (created.isValid() && created.isSame(moment(), 'day')) {
          return created.format('h:mm A');
        }
      }

      // Otherwise show due date info
      const dueDate = task.dueAtStr || task.dueDate;
      if (!dueDate) return '';
      const due = moment(dueDate);
      if (due.isSame(moment(), 'day')) {
        return 'Due Today';
      }
      return due.fromNow();
    },

    getAssigneeName(task) {
      if (task.assignedToName) return task.assignedToName;
      if (!task.assignedToEmpID || !this.employees) return '';
      const assignee = this.employees.find(e =>
        String(e.EmployeeID) === String(task.assignedToEmpID) ||
        String(e.id) === String(task.assignedToEmpID)
      );
      return assignee ? assignee.Name : '';
    },

    getTaskContent(task) {
      // Try to get clean task content without HTML/mentions
      let content = task.taskContent || task.content || '';
      if (typeof content === 'string') {
        // Use a temporary DOM element to properly decode HTML entities
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        // Get text content (this strips HTML tags and decodes entities like &nbsp;)
        content = tempDiv.textContent || tempDiv.innerText || '';
        // Remove extra whitespace
        content = content.replace(/\s+/g, ' ').trim();
      }
      return content || 'Untitled Task';
    },

    async editTask(task) {
      // Navigate to task details via panel or route
      this.$router.push({
        name: 'ToDoID',
        params: { taskID: task.id }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.tasks-summary {
  .stats-row {
    display: flex;
    justify-content: space-evenly;
    padding: var(--widget-stat-padding, 12px 20px);
    border-bottom: 1px solid #e8ecf1;
    margin-bottom: 0;
    min-height: 60px;
  }

  .stat-item {
    display: block !important;
    text-align: center;
    flex: 1;
    min-width: 0;
    padding: 0 8px !important;
    border-left: none !important;
  }

  .stat-value {
    display: block;
    font-size: var(--widget-stat-size, 1.5rem);
    font-weight: 700;
    color: #334155;
    line-height: 1.2;

    &.has-items {
      color: #f59e0b;
    }

    &.completed {
      color: #22c55e;
    }

    &.overdue {
      color: #ef4444;
    }
  }

  .stat-label {
    font-size: 0.6875rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
  }

  .tasks-list {
    max-height: calc(var(--widget-max-items, 3) * 70px);
    overflow-y: auto;
  }

  .task-item {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #f8fafc;
    }
  }

  .task-avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(59, 191, 148, 0.1);
    color: #3abf94;
    border: none !important;

    &.overdue {
      background-color: rgba(239, 68, 68, 0.1);
      color: #ef4444;
    }

    i {
      font-size: 1rem;
    }
  }

  .item-time.overdue {
    color: #ef4444;
    font-weight: 500;
  }

  .item-preview {
    i {
      font-size: 0.75rem;
      opacity: 0.7;
      margin-right: 4px;
    }

    .separator {
      margin: 0 6px;
      color: #cbd5e1;
    }
  }
}
</style>
